/**
 * Import Service - Browser-based file upload and data import
 *
 * This service handles trip data import from file uploads using drag-and-drop
 * and file browser patterns, ensuring data integrity and conflict resolution.
 *
 * Key Responsibilities:
 * - Validate uploaded files (JSON, ZIP, CSV, etc.)
 * - Generate import previews and summaries
 * - Handle multiple import modes (new, merge, replace)
 * - Detect and resolve data conflicts
 * - Coordinate with Firebase for data persistence
 * - Provide real-time progress tracking
 *
 * Event Integration:
 * - Subscribes to: IMPORT_REQUESTED, DATA_VALIDATION_REQUIRED
 * - Publishes: IMPORT_STARTED, IMPORT_PROGRESS, IMPORT_COMPLETED, IMPORT_FAILED, CONFLICT_DETECTED
 */

import { eventBridgeService } from '@trip-planner-monorepo/shared';

class ImportService {
  constructor() {
    this.isInitialized = false;
    this.activeImports = new Map(); // Track ongoing import operations
    this.validators = new Map(); // Schema validators for different data types
    this.conflictResolvers = new Map(); // Conflict resolution strategies
    this.progressCallbacks = new Map(); // Progress tracking callbacks
    this.supportedFormats = ['application/json', 'application/zip', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
  }

  /**
   * Initialize the import service and register event listeners
   */
  init() {
    if (this.isInitialized) {
      console.warn('ImportService already initialized');
      return;
    }

    // Register event listeners
    eventBridgeService.subscribe('IMPORT_REQUESTED', this.handleImportRequest.bind(this));
    eventBridgeService.subscribe('CONFLICT_RESOLUTION_PROVIDED', this.handleConflictResolution.bind(this));

    // Initialize validators and conflict resolvers
    this.initializeValidators();
    this.initializeConflictResolvers();

    this.isInitialized = true;
    console.log('ImportService initialized');
  }

  /**
   * Initialize data validators for different entity types
   */
  initializeValidators() {
    // TODO: Register validators for each data type
    // this.validators.set('trip', new TripValidator());
    // this.validators.set('day', new DayValidator());
    // this.validators.set('place', new PlaceValidator());
    // this.validators.set('accommodation', new AccommodationValidator());
    // this.validators.set('travel', new TravelValidator());
    console.log('Data validators initialized');
  }

  /**
   * Initialize conflict resolution strategies
   */
  initializeConflictResolvers() {
    // TODO: Register conflict resolvers
    // this.conflictResolvers.set('merge', new MergeResolver());
    // this.conflictResolvers.set('replace', new ReplaceResolver());
    // this.conflictResolvers.set('skip', new SkipResolver());
    // this.conflictResolvers.set('rename', new RenameResolver());
    console.log('Conflict resolvers initialized');
  }

  /**
   * Handle import request from UI or other services
   * @param {Object} event - Import request event with source and options
   */
  async handleImportRequest(event) {
    const { importId, userId, source, format, options } = event.payload;

    try {
      // Publish import started event
      eventBridgeService.publish('IMPORT_STARTED', {
        importId,
        userId,
        source,
        format,
        timestamp: new Date().toISOString()
      });

      // Parse and validate source data
      const parsedData = await this.parseSourceData(source, format);
      const validationResult = await this.validateData(parsedData);

      if (!validationResult.isValid) {
        throw new Error(`Data validation failed: ${validationResult.errors.join(', ')}`);
      }

      // Detect conflicts with existing data
      const conflicts = await this.detectConflicts(userId, parsedData);

      if (conflicts.length > 0 && !options.autoResolve) {
        // Publish conflict detection event and wait for user resolution
        eventBridgeService.publish('CONFLICT_DETECTED', {
          importId,
          userId,
          conflicts,
          timestamp: new Date().toISOString()
        });

        // Store import data for later processing
        this.activeImports.set(importId, {
          userId,
          parsedData,
          conflicts,
          status: 'waiting_for_resolution'
        });

        return { status: 'conflicts_detected', conflicts };
      }

      // Process import with conflict resolution
      const importResult = await this.processImport(importId, userId, parsedData, conflicts, options);

      // Publish completion event
      eventBridgeService.publish('IMPORT_COMPLETED', {
        importId,
        userId,
        result: importResult,
        timestamp: new Date().toISOString()
      });

      return importResult;

    } catch (error) {
      // Publish failure event
      eventBridgeService.publish('IMPORT_FAILED', {
        importId,
        userId,
        error: error.message,
        timestamp: new Date().toISOString()
      });

      throw error;
    }
  }

  /**
   * Parse source data based on format
   * @param {any} source - Source data (file, URL, raw data)
   * @param {string} format - Data format (json, csv, etc.)
   * @returns {Object} Parsed data structure
   */
  async parseSourceData(source, format) {
    // TODO: Implement format-specific parsing
    switch (format.toLowerCase()) {
      case 'json':
        return this.parseJsonData(source);
      case 'csv':
        return this.parseCsvData(source);
      case 'backup':
        return this.parseBackupData(source);
      default:
        throw new Error(`Unsupported import format: ${format}`);
    }
  }

  /**
   * Validate parsed data against schemas
   * @param {Object} data - Parsed data to validate
   * @returns {Object} Validation result with errors if any
   */
  async validateData(data) {
    const validationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // TODO: Implement comprehensive data validation
    // - Schema validation for each entity type
    // - Business rule validation
    // - Data consistency checks
    // - Foreign key integrity

    return validationResult;
  }

  /**
   * Detect conflicts between imported data and existing data
   * @param {string} userId - User identifier
   * @param {Object} data - Validated import data
   * @returns {Array} Array of detected conflicts
   */
  async detectConflicts(userId, data) {
    const conflicts = [];

    // TODO: Implement conflict detection logic
    // - Duplicate detection by name, date, location
    // - ID conflicts
    // - Date overlap conflicts for accommodation/travel
    // - Business rule conflicts

    return conflicts;
  }

  /**
   * Process import with conflict resolution
   * @param {string} importId - Import operation identifier
   * @param {string} userId - User identifier
   * @param {Object} data - Validated import data
   * @param {Array} conflicts - Detected conflicts
   * @param {Object} options - Import options including resolution strategies
   * @returns {Object} Import result summary
   */
  async processImport(importId, userId, data, conflicts, options) {
    const result = {
      imported: {
        trips: 0,
        days: 0,
        places: 0,
        accommodation: 0,
        travel: 0,
        documents: 0,
        extras: 0
      },
      skipped: 0,
      conflicts_resolved: conflicts.length
    };

    // TODO: Implement import processing
    // - Apply conflict resolution strategies
    // - Coordinate with domain package services
    // - Update entity relationships
    // - Maintain data integrity

    // Publish progress events during processing
    eventBridgeService.publish('IMPORT_PROGRESS', {
      importId,
      userId,
      progress: 50, // Percentage
      currentOperation: 'Processing days...',
      timestamp: new Date().toISOString()
    });

    return result;
  }

  /**
   * Handle conflict resolution provided by user
   * @param {Object} event - Conflict resolution event
   */
  async handleConflictResolution(event) {
    const { importId, resolutions } = event.payload;

    const importData = this.activeImports.get(importId);
    if (!importData) {
      console.warn(`No active import found for ID: ${importId}`);
      return;
    }

    // Apply user-provided resolutions and continue import
    const options = { ...importData.options, resolutions };
    const result = await this.processImport(
      importId,
      importData.userId,
      importData.parsedData,
      importData.conflicts,
      options
    );

    // Clean up active import
    this.activeImports.delete(importId);

    // Publish completion
    eventBridgeService.publish('IMPORT_COMPLETED', {
      importId,
      userId: importData.userId,
      result,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Parse JSON format data from uploaded file
   * @param {string|File} source - JSON string or File object
   * @returns {Object} Parsed trip data
   */
  async parseJsonData(source) {
    try {
      let jsonString;

      if (source instanceof File) {
        jsonString = await this.readFileAsText(source);
      } else if (typeof source === 'string') {
        jsonString = source;
      } else {
        throw new Error('Invalid source type for JSON parsing');
      }

      const data = JSON.parse(jsonString);

      // Validate basic structure
      if (!data.metadata) {
        throw new Error('Invalid import file: missing metadata section');
      }

      if (!data.trip && !data.trips) {
        throw new Error('Invalid import file: missing trip data');
      }

      return data;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Invalid JSON format in import file');
      }
      throw error;
    }
  }

  /**
   * Parse CSV format data
   */
  async parseCsvData(source) {
    // TODO: Implement CSV parsing logic
    return {};
  }

  /**
   * Parse backup format data
   */
  async parseBackupData(source) {
    // TODO: Implement backup file parsing logic
    return {};
  }

  /**
   * Get import progress for active operations
   * @param {string} importId - Import operation identifier
   * @returns {Object} Progress information
   */
  getImportProgress(importId) {
    return this.activeImports.get(importId) || null;
  }

  /**
   * Validate uploaded file for import
   * @param {File} file - Uploaded file object
   * @param {string} importType - Type of import ('complete' or 'selective')
   * @returns {Object} Validation result
   */
  async validateImportFile(file, importType = 'complete') {
    const validation = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Check file type
    if (!this.supportedFormats.includes(file.type)) {
      validation.isValid = false;
      validation.errors.push(`Unsupported file type: ${file.type}`);
    }

    // Check file size (100MB limit for complete, 50MB for selective)
    const maxSize = importType === 'complete' ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
    if (file.size > maxSize) {
      validation.isValid = false;
      validation.errors.push(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB (max: ${maxSize / 1024 / 1024}MB)`);
    }

    // Basic content validation for JSON files
    if (file.type === 'application/json') {
      try {
        const text = await this.readFileAsText(file);
        const data = JSON.parse(text);

        // Check for required structure
        if (importType === 'complete' && (!data.metadata || !data.trip)) {
          validation.warnings.push('File may not contain complete trip data');
        }
      } catch (error) {
        validation.isValid = false;
        validation.errors.push('Invalid JSON format');
      }
    }

    return validation;
  }

  /**
   * Generate comprehensive import preview from uploaded file
   * @param {File} file - Uploaded file object
   * @returns {Object} Detailed import preview data
   */
  async generateImportPreview(file) {
    try {
      let data;

      if (file.type === 'application/json') {
        const text = await this.readFileAsText(file);
        data = JSON.parse(text);
      } else if (file.type === 'application/zip') {
        data = await this.extractZipData(file);
      } else {
        throw new Error('Preview not available for this file type');
      }

      // Handle both single trip and multi-trip exports
      const tripData = data.trip || (data.trips && data.trips[0]);
      const isMultiTrip = data.trips && data.trips.length > 1;

      const preview = {
        tripName: tripData?.name || 'Unknown Trip',
        dateRange: this.formatDateRange(tripData?.startDate, tripData?.endDate),
        exportDate: this.getExportDate(data.metadata),
        exportVersion: data.metadata?.version || 'Unknown',
        fileSize: this.formatFileSize(file.size),
        isMultiTrip,
        tripCount: isMultiTrip ? data.trips.length : 1,
        dataSummary: this.calculateImportSummary(data),
        hasRelationships: this.hasRelationshipData(data),
        warnings: this.generatePreviewWarnings(data)
      };

      return preview;
    } catch (error) {
      throw new Error(`Failed to generate preview: ${error.message}`);
    }
  }

  /**
   * Upload and import trip data from file
   * @param {Object} options - Import configuration
   * @returns {Object} Import result
   */
  async uploadTripImport(options) {
    const {
      file,
      userId,
      importMode = 'new', // 'new', 'merge', 'replace'
      targetTripId = null,
      onProgress = null
    } = options;

    const importId = `import-${Date.now()}`;

    try {
      // Track progress callback
      if (onProgress) {
        this.progressCallbacks.set(importId, onProgress);
      }

      this.updateProgress(importId, 10, 'Reading file...');

      // Parse file data
      const data = await this.parseFileData(file);

      this.updateProgress(importId, 30, 'Validating data...');

      // Validate data structure
      const validation = await this.validateTripData(data);
      if (!validation.isValid) {
        throw new Error(`Data validation failed: ${validation.errors.join(', ')}`);
      }

      this.updateProgress(importId, 50, 'Checking for conflicts...');

      // Handle import based on mode
      let result;
      switch (importMode) {
        case 'new':
          result = await this.createNewTripImport(data, userId, importId);
          break;
        case 'merge':
          result = await this.mergeWithExistingTrip(data, targetTripId, userId, importId);
          break;
        case 'replace':
          result = await this.replaceExistingTrip(data, targetTripId, userId, importId);
          break;
        default:
          throw new Error(`Invalid import mode: ${importMode}`);
      }

      this.updateProgress(importId, 100, 'Import complete');

      // Cleanup
      this.progressCallbacks.delete(importId);

      return {
        success: true,
        summary: this.generateImportSummary(result),
        tripId: result.tripId,
        mode: importMode
      };

    } catch (error) {
      this.progressCallbacks.delete(importId);
      console.error('Import failed:', error);
      throw error;
    }
  }

  /**
   * Upload selective import for specific data types
   * @param {Object} options - Selective import configuration
   * @returns {Object} Import result
   */
  async uploadSelectiveImport(options) {
    const {
      file,
      tripId,
      userId,
      scope, // 'pages', 'days', etc.
      onProgress = null
    } = options;

    const importId = `selective-import-${Date.now()}`;

    try {
      if (onProgress) {
        this.progressCallbacks.set(importId, onProgress);
      }

      this.updateProgress(importId, 10, 'Processing selective import...');

      // Parse and validate file
      const data = await this.parseFileData(file);
      const scopeData = this.extractScopeData(data, scope);

      this.updateProgress(importId, 50, `Importing ${scope}...`);

      // Import specific data type
      const result = await this.importScopeData(tripId, userId, scope, scopeData);

      this.updateProgress(importId, 100, 'Selective import complete');

      this.progressCallbacks.delete(importId);

      return {
        success: true,
        summary: `${result.count} ${scope} imported successfully`,
        scope,
        count: result.count
      };

    } catch (error) {
      this.progressCallbacks.delete(importId);
      throw error;
    }
  }

  /**
   * Read file content as text
   * @param {File} file - File object
   * @returns {Promise<string>} File content
   */
  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  /**
   * Extract data from ZIP file
   * @param {File} file - ZIP file
   * @returns {Object} Extracted data
   */
  async extractZipData(file) {
    // TODO: Implement ZIP extraction using JSZip
    // For now, throw error as placeholder
    throw new Error('ZIP import not yet implemented');
  }

  /**
   * Parse file data based on file type
   * @param {File} file - Uploaded file
   * @returns {Object} Parsed data
   */
  async parseFileData(file) {
    if (file.type === 'application/json') {
      const text = await this.readFileAsText(file);
      return this.normalizeImportedData(JSON.parse(text));
    } else if (file.type === 'application/zip') {
      return this.normalizeImportedData(await this.extractZipData(file));
    } else {
      throw new Error(`Unsupported file type: ${file.type}`);
    }
  }

  /**
   * jsonFormatHandler.process() nests entity arrays under `entities.<type>` for
   * display/schema purposes; every downstream import method here (remapEntityIds,
   * createTripWithData) reads them flat (data.days, data.places, ...). Without this,
   * a trip exported via the JSON download and re-imported creates an empty trip shell.
   * @param {Object} data - Parsed import data, either flat or entities-nested
   * @returns {Object} Data with entity arrays guaranteed at the top level
   */
  normalizeImportedData(data) {
    if (!data?.entities) {
      return data;
    }
    const entityTypes = ['days', 'places', 'accommodation', 'travel', 'documents', 'extras'];
    const normalized = { ...data };
    entityTypes.forEach((type) => {
      if (!Array.isArray(normalized[type]) && Array.isArray(data.entities[type])) {
        normalized[type] = data.entities[type];
      }
    });
    return normalized;
  }

  /**
   * Validate trip data structure comprehensively
   * @param {Object} data - Trip data
   * @returns {Object} Validation result
   */
  async validateTripData(data) {
    const validation = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Check required structure
    if (!data.metadata) {
      validation.errors.push('Missing metadata section');
    } else {
      // Validate metadata fields
      if (!data.metadata.version) {
        validation.warnings.push('Export version not specified');
      }
      if (!data.metadata.createdAt && !data.metadata.exportDate) {
        validation.warnings.push('Export date not specified');
      }
    }

    // Handle both single trip and multi-trip exports
    const tripData = data.trip || (data.trips && data.trips[0]);
    if (!tripData) {
      validation.errors.push('Missing trip information');
    } else {
      // Validate trip fields
      if (!tripData.name) {
        validation.warnings.push('Trip name is missing');
      }
      if (!tripData.startDate) {
        validation.warnings.push('Trip start date is missing');
      }
    }

    // Validate entity data arrays
    const entityTypes = ['days', 'places', 'accommodation', 'travel', 'documents', 'extras'];
    entityTypes.forEach(type => {
      if (data[type] && !Array.isArray(data[type])) {
        validation.errors.push(`${type} data must be an array`);
      }
    });

    // Check for content blocks structure in days
    if (data.days && Array.isArray(data.days)) {
      data.days.forEach((day, index) => {
        if (day.contentBlocks && !Array.isArray(day.contentBlocks)) {
          validation.warnings.push(`Day ${index + 1}: contentBlocks should be an array`);
        }
      });
    }

    validation.isValid = validation.errors.length === 0;
    return validation;
  }

  /**
   * Create new trip from import data
   * @param {Object} data - Import data
   * @param {string} userId - User identifier
   * @param {string} importId - Import operation ID
   * @returns {Object} Import result
   */
  async createNewTripImport(data, userId, importId) {
    this.updateProgress(importId, 70, 'Creating new trip...');

    // Generate new trip ID
    const tripId = this.generateTripId();

    // Remap all entity IDs to prevent conflicts
    const remappedData = this.remapEntityIds(data, tripId);

    // Create trip and all associated data
    const result = await this.createTripWithData(remappedData, userId);

    return {
      tripId,
      mode: 'new',
      created: result.counts
    };
  }

  /**
   * Merge import data with existing trip
   * @param {Object} data - Import data
   * @param {string} tripId - Target trip ID
   * @param {string} userId - User identifier
   * @param {string} importId - Import operation ID
   * @returns {Object} Import result
   */
  async mergeWithExistingTrip(data, tripId, userId, importId) {
    this.updateProgress(importId, 70, 'Merging with existing trip...');

    // Detect conflicts with existing data
    const conflicts = await this.detectTripConflicts(data, tripId);

    // Apply conflict resolution
    const resolvedData = await this.resolveConflicts(data, conflicts);

    // Merge data with existing trip
    const result = await this.mergeDataWithTrip(resolvedData, tripId, userId);

    return {
      tripId,
      mode: 'merge',
      merged: result.counts,
      conflicts: conflicts.length
    };
  }

  /**
   * Replace existing trip with import data
   * @param {Object} data - Import data
   * @param {string} tripId - Target trip ID
   * @param {string} userId - User identifier
   * @param {string} importId - Import operation ID
   * @returns {Object} Import result
   */
  async replaceExistingTrip(data, tripId, userId, importId) {
    this.updateProgress(importId, 70, 'Replacing existing trip...');

    // Backup current trip data
    const backup = await this.createTripBackup(tripId);

    // Clear existing trip data
    await this.clearTripData(tripId);

    // Import new data with existing trip ID
    const remappedData = this.remapToExistingTrip(data, tripId);
    const result = await this.createTripWithData(remappedData, userId);

    return {
      tripId,
      mode: 'replace',
      replaced: result.counts,
      backup: backup.backupId
    };
  }

  /**
   * Update import progress and notify callbacks
   * @param {string} importId - Import identifier
   * @param {number} progress - Progress percentage (0-100)
   * @param {string} details - Progress details
   */
  updateProgress(importId, progress, details) {
    const callback = this.progressCallbacks.get(importId);
    if (callback) {
      callback({ progress, details });
    }

    eventBridgeService.publish('IMPORT_PROGRESS', {
      importId,
      progress,
      details,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get export date from metadata
   * @param {Object} metadata - Export metadata
   * @returns {string} Formatted export date
   */
  getExportDate(metadata) {
    const exportDate = metadata?.createdAt || metadata?.exportDate;
    if (!exportDate) return 'Unknown';

    try {
      return new Date(exportDate).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  }

  /**
   * Calculate comprehensive import summary
   * @param {Object} data - Import data
   * @returns {Object} Summary statistics
   */
  calculateImportSummary(data) {
    const summary = {
      Days: 0,
      Places: 0,
      Accommodation: 0,
      Travel: 0,
      Documents: 0,
      Extras: 0
    };

    // Handle multi-trip exports
    if (data.trips && Array.isArray(data.trips)) {
      data.trips.forEach(tripData => {
        summary.Days += Array.isArray(tripData.days) ? tripData.days.length : 0;
        summary.Places += Array.isArray(tripData.places) ? tripData.places.length : 0;
        summary.Accommodation += Array.isArray(tripData.accommodation) ? tripData.accommodation.length : 0;
        summary.Travel += Array.isArray(tripData.travel) ? tripData.travel.length : 0;
        summary.Documents += Array.isArray(tripData.documents) ? tripData.documents.length : 0;
        summary.Extras += Array.isArray(tripData.extras) ? tripData.extras.length : 0;
      });
    } else {
      // Single trip export
      summary.Days = Array.isArray(data.days) ? data.days.length : 0;
      summary.Places = Array.isArray(data.places) ? data.places.length : 0;
      summary.Accommodation = Array.isArray(data.accommodation) ? data.accommodation.length : 0;
      summary.Travel = Array.isArray(data.travel) ? data.travel.length : 0;
      summary.Documents = Array.isArray(data.documents) ? data.documents.length : 0;
      summary.Extras = Array.isArray(data.extras) ? data.extras.length : 0;
    }

    return summary;
  }

  /**
   * Check if import data has relationship information
   * @param {Object} data - Import data
   * @returns {boolean} True if relationships exist
   */
  hasRelationshipData(data) {
    return !!(data.relationships ||
      (data.days && data.days.some(day => day.contentBlocks && day.contentBlocks.length > 0)));
  }

  /**
   * Generate preview warnings for potential issues
   * @param {Object} data - Import data
   * @returns {Array} Array of warning messages
   */
  generatePreviewWarnings(data) {
    const warnings = [];

    // Check for missing trip name
    const tripData = data.trip || (data.trips && data.trips[0]);
    if (!tripData?.name) {
      warnings.push('Trip name is missing');
    }

    // Check for missing dates
    if (!tripData?.startDate) {
      warnings.push('Trip start date is missing');
    }

    // Check for empty data sections
    const entityTypes = ['days', 'places', 'accommodation', 'travel'];
    const emptySections = entityTypes.filter(type => {
      const items = data[type] || [];
      return !Array.isArray(items) || items.length === 0;
    });

    if (emptySections.length === entityTypes.length) {
      warnings.push('Import file appears to contain no trip data');
    }

    // Check for old export format
    if (!data.metadata?.version || data.metadata.version < '2.0.0') {
      warnings.push('This file was exported with an older version and may have compatibility issues');
    }

    return warnings;
  }

  /**
   * Format date range for display
   * @param {string} startDate - Start date
   * @param {string} endDate - End date
   * @returns {string} Formatted date range
   */
  formatDateRange(startDate, endDate) {
    if (!startDate && !endDate) return 'No dates specified';
    if (!endDate) return new Date(startDate).toLocaleDateString();
    if (!startDate) return `Until ${new Date(endDate).toLocaleDateString()}`;

    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();
    return `${start} - ${end}`;
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Generate import summary
   * @param {Object} result - Import result
   * @returns {string} Summary message
   */
  generateImportSummary(result) {
    const { mode, created, merged, replaced } = result;
    const counts = created || merged || replaced || {};

    const items = Object.entries(counts)
      .filter(([key, value]) => value > 0)
      .map(([key, value]) => `${value} ${key}`)
      .join(', ');

    return `${mode === 'new' ? 'Created' : mode === 'merge' ? 'Merged' : 'Replaced'} trip with: ${items}`;
  }

  /**
   * Generate unique trip ID
   * @returns {string} New trip ID
   */
  generateTripId() {
    return `trip-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Remap entity IDs to prevent conflicts with existing data
   * @param {Object} data - Import data
   * @param {string} tripId - New trip ID
   * @returns {Object} Data with remapped IDs
   */
  remapEntityIds(data, tripId) {
    const idMapping = new Map();
    const remappedData = JSON.parse(JSON.stringify(data)); // Deep clone

    // Generate new trip ID if not provided
    if (remappedData.trip) {
      const oldTripId = remappedData.trip.id;
      remappedData.trip.id = tripId;
      idMapping.set(oldTripId, tripId);
    }

    // Remap entity IDs
    const entityTypes = ['days', 'places', 'accommodation', 'travel', 'documents', 'extras'];

    entityTypes.forEach(entityType => {
      if (Array.isArray(remappedData[entityType])) {
        remappedData[entityType].forEach(entity => {
          if (entity.id) {
            const oldId = entity.id;
            const newId = `${entityType.slice(0, -1)}-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            entity.id = newId;
            idMapping.set(oldId, newId);
          }
          // Update tripId reference
          entity.tripId = tripId;
        });
      }
    });

    // Update content block references
    if (Array.isArray(remappedData.days)) {
      remappedData.days.forEach(day => {
        if (Array.isArray(day.contentBlocks)) {
          day.contentBlocks.forEach(block => {
            // Update accommodation references
            if (block.type === 'accommodation' && block.accommodationId) {
              const newId = idMapping.get(block.accommodationId);
              if (newId) {
                block.accommodationId = newId;
              }
            }
            // Update place references
            if (block.type === 'place' && block.placeId) {
              const newId = idMapping.get(block.placeId);
              if (newId) {
                block.placeId = newId;
              }
            }
          });
        }
      });
    }

    // Store ID mapping for relationship reconstruction
    remappedData._idMapping = idMapping;

    return remappedData;
  }

  /**
   * Create trip and all associated data in Firebase
   * @param {Object} data - Remapped import data
   * @param {string} userId - User identifier
   * @returns {Object} Creation result with counts
   */
  async createTripWithData(data, userId) {
    // Import Firebase functions
    const {
      addTrip,
      addDay,
      addPlace,
      addAccommodation,
      addTravel,
      connectionRegistryService,
      linkRegistryConfigService
    } = await import('@trip-planner-monorepo/shared');

    const counts = {
      trips: 0,
      days: 0,
      places: 0,
      accommodation: 0,
      travel: 0,
      documents: 0,
      extras: 0,
      links: 0
    };

    // remapEntityIds() (run before this method, for every import mode) already built
    // originalId -> syntheticId in data._idMapping — but the synthetic id is discarded
    // below (Firestore assigns the real id). Compose original -> synthetic -> real so
    // links.json (which references original ids) can be imported against real ids.
    const syntheticToOriginal = new Map();
    if (data._idMapping instanceof Map) {
      data._idMapping.forEach((syntheticId, originalId) => {
        syntheticToOriginal.set(syntheticId, originalId);
      });
    }
    const originalToRealId = {};
    const recordRealId = (syntheticOrOriginalId, realId) => {
      if (!syntheticOrOriginalId) return;
      const originalId = syntheticToOriginal.get(syntheticOrOriginalId) || syntheticOrOriginalId;
      originalToRealId[originalId] = realId;
    };

    try {
      // Create trip first
      if (data.trip) {
        const oldTripId = data.trip.id;
        const tripData = {
          ...data.trip,
          importedAt: new Date().toISOString(),
          originalExport: {
            version: data.metadata?.version,
            exportDate: data.metadata?.createdAt || data.metadata?.exportDate
          }
        };
        delete tripData.id; // Let Firebase generate the ID

        const tripId = await addTrip(userId, tripData);
        recordRealId(oldTripId, tripId);
        data.trip.id = tripId; // Update for entity references
        counts.trips = 1;
      }

      const tripId = data.trip.id;

      // Create places first (other entities may reference them)
      if (Array.isArray(data.places)) {
        for (const place of data.places) {
          const placeData = { ...place };
          const oldId = placeData.id;
          delete placeData.id;
          delete placeData.tripId;

          const newId = await addPlace(userId, tripId, placeData);
          recordRealId(oldId, newId);
          counts.places++;
        }
      }

      // Create accommodation
      if (Array.isArray(data.accommodation)) {
        for (const accommodation of data.accommodation) {
          const accommodationData = { ...accommodation };
          const oldId = accommodationData.id;
          delete accommodationData.id;
          delete accommodationData.tripId;

          const newId = await addAccommodation(userId, tripId, accommodationData);
          recordRealId(oldId, newId);
          counts.accommodation++;
        }
      }

      // Create travel
      if (Array.isArray(data.travel)) {
        for (const travel of data.travel) {
          const travelData = { ...travel };
          const oldId = travelData.id;
          delete travelData.id;
          delete travelData.tripId;

          const newId = await addTravel(userId, tripId, travelData);
          recordRealId(oldId, newId);
          counts.travel++;
        }
      }

      // Create days (after other entities for content block references)
      if (Array.isArray(data.days)) {
        for (const day of data.days) {
          const dayData = { ...day };
          const oldId = dayData.id;
          delete dayData.id;
          delete dayData.tripId;

          const newId = await addDay(userId, tripId, dayData);
          recordRealId(oldId, newId);
          counts.days++;
        }
      }

      // TODO: Handle documents and extras when their Firebase integration is complete
      if (Array.isArray(data.documents)) {
        counts.documents = data.documents.length; // Placeholder count
      }
      if (Array.isArray(data.extras)) {
        counts.extras = data.extras.length; // Placeholder count
      }

      // Import connection registry links (item-to-item relationships), remapped to
      // the real Firestore ids created above. Without this, an exported trip loses
      // every hotel<->place / stay<->day / document<->item relationship on import.
      if (Array.isArray(data.links) && data.links.length > 0) {
        const linkResult = await connectionRegistryService.importLinkDocuments(
          userId,
          tripId,
          data.links,
          originalToRealId
        );
        counts.links = linkResult.imported;
      }

      // Restore the source trip's registry config, or bootstrap fresh defaults
      // (extracting legacy links only if none were imported above).
      if (data._meta?.linkRegistry) {
        await linkRegistryConfigService.setLinkRegistryConfig(userId, tripId, data._meta.linkRegistry);
      } else {
        await linkRegistryConfigService.bootstrapNewTripRegistry(userId, tripId, {
          extractLegacyLinks: counts.links === 0
        });
      }

      return { counts, tripId };
    } catch (error) {
      console.error('Error creating trip with data:', error);
      throw new Error(`Failed to import trip data: ${error.message}`);
    }
  }

  /**
   * Detect conflicts between import data and existing trip data
   * @param {Object} data - Import data
   * @param {string} tripId - Existing trip ID
   * @returns {Array} Array of detected conflicts
   */
  async detectTripConflicts(data, tripId) {
    const conflicts = [];

    try {
      // Import Firebase functions
      const {
        getTrip,
        getDays,
        getAccommodation
      } = await import('@trip-planner-monorepo/shared');

      // Get existing trip data
      const existingTrip = await getTrip(data.userId || 'temp', tripId);

      // Check trip name conflicts
      if (data.trip && existingTrip.name === data.trip.name) {
        conflicts.push({
          id: `trip-name-conflict-${Date.now()}`,
          type: 'duplicate_trip_name',
          description: `Trip name "${data.trip.name}" already exists`,
          existing: { name: existingTrip.name, id: existingTrip.id },
          incoming: { name: data.trip.name },
          resolutionOptions: ['rename', 'replace', 'skip']
        });
      }

      // Check accommodation date conflicts
      if (Array.isArray(data.accommodation)) {
        const existingAccommodations = await getAccommodation(data.userId || 'temp', tripId);

        data.accommodation.forEach(newAcc => {
          existingAccommodations.forEach(existingAcc => {
            // Check for date overlaps
            if (this.datesOverlap(newAcc.checkIn, newAcc.checkOut, existingAcc.checkIn, existingAcc.checkOut)) {
              conflicts.push({
                id: `accommodation-date-conflict-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                type: 'overlapping_accommodation_dates',
                description: `Accommodation dates overlap: "${newAcc.name}" with existing "${existingAcc.name}"`,
                existing: {
                  name: existingAcc.name,
                  dates: `${existingAcc.checkIn} to ${existingAcc.checkOut}`
                },
                incoming: {
                  name: newAcc.name,
                  dates: `${newAcc.checkIn} to ${newAcc.checkOut}`
                },
                resolutionOptions: ['adjust_dates', 'force_import', 'skip']
              });
            }
          });
        });
      }

      return conflicts;
    } catch (error) {
      console.error('Error detecting conflicts:', error);
      return []; // Return empty array to allow import to continue
    }
  }

  /**
   * Check if two date ranges overlap
   * @param {string} start1 - First range start
   * @param {string} end1 - First range end
   * @param {string} start2 - Second range start
   * @param {string} end2 - Second range end
   * @returns {boolean} True if dates overlap
   */
  datesOverlap(start1, end1, start2, end2) {
    if (!start1 || !end1 || !start2 || !end2) return false;

    const date1Start = new Date(start1);
    const date1End = new Date(end1);
    const date2Start = new Date(start2);
    const date2End = new Date(end2);

    return date1Start < date2End && date2Start < date1End;
  }

  /**
   * Resolve conflicts based on user choices
   * @param {Object} data - Import data
   * @param {Array} conflicts - Detected conflicts
   * @returns {Object} Resolved data
   */
  resolveConflicts(data, conflicts) {
    // This is a placeholder - conflict resolution would be handled
    // by the UI and passed back through the event system
    return Promise.resolve(data);
  }

  /**
   * Merge import data with existing trip
   * @param {Object} data - Import data
   * @param {string} tripId - Target trip ID
   * @param {string} userId - User identifier
   * @returns {Object} Merge result
   */
  async mergeDataWithTrip(data, tripId, userId) {
    // Remap IDs to prevent conflicts
    const remappedData = this.remapEntityIds(data, tripId);

    // Create entities without creating a new trip
    const result = await this.createTripWithData({ ...remappedData, trip: null }, userId);

    return { counts: result.counts };
  }

  /**
   * Create backup of existing trip before replacement
   * @param {string} tripId - Trip ID to backup
   * @returns {Object} Backup result
   */
  async createTripBackup(tripId) {
    // This would create a backup using the export service
    const backupId = `backup-${tripId}-${Date.now()}`;
    console.log(`Creating backup ${backupId} for trip ${tripId}`);
    return Promise.resolve({ backupId });
  }

  /**
   * Clear existing trip data before replacement
   * @param {string} tripId - Trip ID to clear
   * @returns {Promise} Clear operation result
   */
  async clearTripData(tripId) {
    // This would delete all entities associated with the trip
    console.log(`Clearing data for trip ${tripId}`);
    return Promise.resolve();
  }

  /**
   * Remap import data to use existing trip ID
   * @param {Object} data - Import data
   * @param {string} tripId - Existing trip ID
   * @returns {Object} Remapped data
   */
  remapToExistingTrip(data, tripId) {
    return this.remapEntityIds(data, tripId);
  }

  /**
   * Extract data for specific scope
   * @param {Object} data - Full import data
   * @param {string} scope - Data scope to extract
   * @returns {Array} Scope-specific data
   */
  extractScopeData(data, scope) {
    return data[scope] || [];
  }

  /**
   * Import specific data type to existing trip
   * @param {string} tripId - Target trip ID
   * @param {string} userId - User identifier
   * @param {string} scope - Data type scope
   * @param {Array} data - Data to import
   * @returns {Object} Import result
   */
  async importScopeData(tripId, userId, scope, data) {
    // This would import specific entity types
    console.log(`Importing ${data.length} ${scope} items to trip ${tripId}`);
    return Promise.resolve({ count: data.length });
  }

  /**
   * Cancel an ongoing import operation
   * @param {string} importId - Import operation identifier
   */
  cancelImport(importId) {
    const importInfo = this.activeImports.get(importId);
    if (importInfo) {
      importInfo.cancelled = true;
      this.activeImports.delete(importId);
      this.progressCallbacks.delete(importId);

      eventBridgeService.publish('IMPORT_CANCELLED', {
        importId,
        timestamp: new Date().toISOString()
      });
    }
  }
}

// Create singleton instance
const importService = new ImportService();

export { importService };
export default importService;